<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Station Capacity Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyCapacityFilter"
              @reset="resetCapacityFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingCapacityFilter.startDate"
                  :endDate="editingCapacityFilter.endDate"
                  @updateStartDate="editingCapacityFilter.startDate = $event"
                  @updateEndDate="editingCapacityFilter.endDate = $event"
                />
                <FormHorizontalRule />
                Production stations
                <FormMultiSelect v-model="editingCapacityFilter.productionStationIdList" :options="productionStationLookup" />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedCapacityFilter.startDate | formatDate}} - {{appliedCapacityFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Production Stations" highlight>
              {{capacitySummaryData.productionStationNameList.join(', ')}}
              <template v-slot:info>
                <div v-if="hasUOMTypeMismatch">
                  <i class="fas fa-exclamation-triangle mr-1 text-danger"></i>
                  <span>Multiple UoM types detected. Please use stations with matching UoM types.</span>
                </div>
              </template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Daily Completed Units">
              {{formatDecimal(capacitySummaryData.averageCompletedUnits, 2, true)}}<span v-html="capacitySummaryData.uomTypeSuffix" />
              <template v-slot:info>Per day over {{capacitySummaryData.dataCompletedDayCount}} day{{displayCountPlural(capacitySummaryData.dataCompletedDayCount)}} units were completed</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Daily Capacity Reached">
              {{formatPercent(capacitySummaryData.averageCompletedCapacity, 2, true)}}
              <template v-slot:info>For {{formatDecimal(capacitySummaryData.averageCapacityUnits)}}<span v-html="capacitySummaryData.uomTypeSuffix" /> capacity per day over {{capacitySummaryData.dataCompletedDayCount}} day{{displayCountPlural(capacitySummaryData.dataCompletedDayCount)}} units were completed</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        
      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Production Capacity"
          :series="capacityDailyValueData.series"
          :xAxis="capacityDailyValueData.xAxis"
          :yAxis="capacityDailyValueData.yAxis"
          :legend="capacityDailyValueData.legend"
          :options="capacityDailyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

import { UOMTypeUnitsSafeLookup, UOMTypeUnitsLookup } from '@/store/types'

export default {
  name: 'ProductionDailyCapacityChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      capacitySummaryData: {
        productionStationNameList: [],
        dataDayCount: 0,
        dataCompletedDayCount: 0,
        totalCompletedUnits: 0,
        averageCompletedUnits: 0,
        averageCapacityUnits: 0,
        uomType: '',
        uomTypeList: [],
        averageCompletedCapacity: 0,
        averageAvailableCapacity: 0,
      },
      capacityDailyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultCapacityFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().add(15, 'days').endOf('day').toDate().toISOString(),
        productionStationIdList: []
      },
      editingCapacityFilter: {},
      appliedCapacityFilter: {},

      productionStationLookup: []
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
    hasUOMTypeMismatch() {
      return this.capacitySummaryData.uomTypeList.length > 1
    }
  },
  mounted() {
    this.editingCapacityFilter = _.clone(this.defaultCapacityFilter)
    this.appliedCapacityFilter = _.clone(this.defaultCapacityFilter)

    // Note: Only load chart data when we have a production station set
    this.requestData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyCapacityFilter() {
      this.appliedCapacityFilter = _.clone(this.editingCapacityFilter)

      this.requestChartData()
    },
    resetCapacityFilter() {
      this.editingCapacityFilter = _.clone(this.defaultCapacityFilter)
      this.appliedCapacityFilter = _.clone(this.defaultCapacityFilter)

      this.requestChartData()
    },
    requestData() {
      // Load the production stations
      this.$apiService.productionService.getProductionStationList()
      .then(response => {
        this.productionStationLookup = response.data
        .filter(productionStation => productionStation.isActive)
        .map(productionStation => {
          return {
            value: productionStation.id,
            name: productionStation.name
          }
        })

        // Apply the 1st station to the default filter
        if(this.productionStationLookup.length > 0) {
          this.defaultCapacityFilter.productionStationIdList = [this.productionStationLookup[0].value]
          this.resetCapacityFilter()
        }
      })
      .catch(err => {
        this.displayNetworkError(err, 'Could not load data')
      })
    },
    requestChartData() {
      var capacityFilter = {
        type: 'daily',
        startDate: this.appliedCapacityFilter.startDate,
        endDate: this.appliedCapacityFilter.endDate,
        productionStationIdList: this.appliedCapacityFilter.productionStationIdList
      }

      let widgetComp = this

      Promise.all([
        this.$apiService.analyticsService.getProductionCapacityChartData(capacityFilter)
      ])
      .then(results => {
        let chartData = results[0].data

        var uomType = ''
        var uomTypeList = []
        if(chartData.length > 0) {
          uomType = chartData[0].uomType
          uomTypeList = chartData[0].uomTypeList
        }
        var availableUnits = chartData.reduce((cur, item) => Math.max(cur, item.availableUnits), 0)
        var uomSuffix = this.formatLookupName(uomType, UOMTypeUnitsSafeLookup)

        var completedDayList = chartData.filter(item => item.totalCompletedUnits > 0)

        // Summary data
        this.capacitySummaryData.productionStationNameList = capacityFilter.productionStationIdList.map(id => this.formatLookupName(id, this.productionStationLookup))
        this.capacitySummaryData.uomType = uomType
        this.capacitySummaryData.uomTypeList = uomTypeList
        this.capacitySummaryData.uomTypeSuffix = this.formatLookupName(uomType, UOMTypeUnitsLookup)
        this.capacitySummaryData.dataDayCount = chartData.length
        this.capacitySummaryData.dataCompletedDayCount = completedDayList.length
        this.capacitySummaryData.totalCompletedUnits = chartData.reduce((cur, item) => cur + item.totalCompletedUnits, 0)

        var totalCapacityUnits = completedDayList.reduce((cur, item) => cur + item.availableUnits, 0)
        
        this.capacitySummaryData.averageCapacityUnits = 0
        this.capacitySummaryData.averageCompletedUnits = 0
        this.capacitySummaryData.averageCompletedCapacity = 0

        if(this.capacitySummaryData.dataCompletedDayCount > 0) {
          this.capacitySummaryData.averageCapacityUnits = totalCapacityUnits / this.capacitySummaryData.dataCompletedDayCount
        }
        if(this.capacitySummaryData.dataCompletedDayCount > 0) {
          this.capacitySummaryData.averageCompletedUnits = this.capacitySummaryData.totalCompletedUnits / this.capacitySummaryData.dataCompletedDayCount
        }
        
        if(totalCapacityUnits > 0)
          this.capacitySummaryData.averageCompletedCapacity = (this.capacitySummaryData.totalCompletedUnits / totalCapacityUnits) * 100

        this.capacityDailyValueData.options = {
          tooltip: {
            formatter: (params) => {
              if(params.length == 0)
                return ''

              var dayCapacity = this.capacitySummaryData.averageCapacityUnits

              let lineList = params.map(series => {
                let value = series.value
                if(this.capacityDailyValueData.series[series.seriesIndex].tooltip && this.capacityDailyValueData.series[series.seriesIndex].tooltip.valueFormatter)
                  value = this.capacityDailyValueData.series[series.seriesIndex].tooltip.valueFormatter(value)

                var percent = 0
                if(dayCapacity > 0)
                  percent = (series.value / dayCapacity) * 100

                return `${series.marker}${series.seriesName}<span style="float: right; margin-left: 20px"><b>${value}</b> (${this.formatPercent(percent, 0)})</span>`
              })

              return `${params[0].axisValueLabel}<br />` + lineList.join('<br />') + `<br /><br />Day Capacity<span style="float: right; margin-left: 20px"><b>${dayCapacity} ${uomSuffix}</b></span>`
            }
          }
        }

        this.capacityDailyValueData.legend = {}
        this.capacityDailyValueData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM'))
        }
        this.capacityDailyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return `${value} ${uomSuffix}`
              }
            }
          }
        ]
        this.capacityDailyValueData.series = []
        this.capacityDailyValueData.series.push({
          type: 'bar',
          name: 'Completed Units',
          stack: 'total',
          data: chartData.map(chartEntry => chartEntry.totalCompletedUnits),
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatDecimal(value, 2) + ` ${uomSuffix}`
            }
          },
          markLine: {
            label: 'Available Capacity Units',
            data: [{ yAxis: availableUnits, name: 'Capacity' }]
          },
          itemStyle: {
            color: '#91CC75'
          }
        })
        this.capacityDailyValueData.series.push({
          type: 'bar',
          name: 'Pending Units',
          stack: 'total',
          data: chartData.map(chartEntry => chartEntry.totalPendingUnits),
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatDecimal(value, 2) + ` ${uomSuffix}`
            }
          },
          itemStyle: {
            color: '#FAC858'
          }
        })
        this.capacityDailyValueData.series.push({
          type: 'bar',
          name: 'Unmet Capacity',
          stack: 'total',
          data: chartData.map(chartEntry => chartEntry.unmetCapacity),
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatDecimal(value, 2) + ` ${uomSuffix}`
            }
          },
          itemStyle: {
            color: '#5470C6'
          }
        })        
        
      })
    },
  }
}
</script>